import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { CircleAlert, RefreshCw, X } from "lucide-react";
import {
  CASE_DEPTHS, CASE_ISSUES, CASE_NOTES_MAX, CASE_OUTCOMES, DEPTH_LABELS, detectFeedbackConflict, ISSUE_LABELS,
  issuesRequired, OUTCOME_LABELS, SKIP_STREAK_WARNING, type CaseDepth, type CaseIssue, type CaseOutcome
} from "../shared/case-feedback";
import type { CaseFeedbackInput } from "../shared/contracts";
import { DictationButton } from "./VoiceInput";

/**
 * Avaliação ao concluir o caso: três respostas de um clique, chips quando a nota é baixa e um
 * comentário opcional. Enviar ou pular encerra o caso; fechar no X apenas adia.
 */
export function CaseFeedbackDialog({ caseTitle, skipStreak, onSubmit, onSkip, onClose }: {
  caseTitle: string;
  skipStreak: number;
  onSubmit: (input: CaseFeedbackInput) => Promise<void>;
  onSkip: () => Promise<void>;
  onClose: () => void;
}) {
  const [outcome, setOutcome] = useState<CaseOutcome | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [issues, setIssues] = useState<CaseIssue[]>([]);
  const [depth, setDepth] = useState<CaseDepth | undefined>();
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const needsIssues = rating !== null && issuesRequired(rating);
  const ready = outcome !== null && rating !== null && (!needsIssues || issues.length > 0);
  const answers = ready ? { outcome: outcome!, rating: rating!, issues, depth } : null;
  const conflict = answers ? detectFeedbackConflict(answers) : false;

  function toggleIssue(issue: CaseIssue) {
    setConfirming(false);
    setIssues((current) => {
      if (current.includes(issue)) return current.filter((item) => item !== issue);
      // "Nada" e um problema concreto se excluem.
      return issue === "none" ? ["none"] : [...current.filter((item) => item !== "none"), issue];
    });
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try { await action(); } finally { setBusy(false); }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!answers) return;
    if (conflict && !confirming) { setConfirming(true); return; }
    void run(() => onSubmit({ ...answers, notes: notes.trim() || undefined }));
  }

  return <motion.div className="modal-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose}>
    <motion.form role="dialog" aria-modal="true" aria-labelledby="case-feedback-title" className="new-case-dialog case-feedback-dialog"
      initial={{ opacity: 0, y: 12, scale: .985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: .99 }}
      onMouseDown={(event) => event.stopPropagation()} onSubmit={submit}>
      <div className="dialog-head">
        <div><p className="eyebrow ink">CONCLUIR CASO</p><h2 id="case-feedback-title">Como foi este resultado?</h2><small className="case-feedback-subject">{caseTitle}</small></div>
        <button aria-label="Fechar" type="button" className="icon-button" onClick={onClose}><X size={19} /></button>
      </div>

      <fieldset className="feedback-question">
        <legend>O que você fez com o resultado? <em>obrigatório</em></legend>
        <div className="segmented">{CASE_OUTCOMES.map((item) => <button type="button" key={item} aria-pressed={outcome === item}
          className={outcome === item ? "selected" : ""} onClick={() => { setOutcome(item); setConfirming(false); }}>{OUTCOME_LABELS[item]}</button>)}</div>
      </fieldset>

      <fieldset className="feedback-question">
        <legend>Nota técnica — precisão e confiabilidade <em>obrigatório</em></legend>
        <div className="segmented rating">{[1, 2, 3, 4, 5].map((value) => <button type="button" key={value} aria-pressed={rating === value}
          aria-label={`Nota ${value}`} className={rating === value ? "selected" : ""} onClick={() => { setRating(value); setConfirming(false); }}>{value}</button>)}</div>
      </fieldset>

      <fieldset className="feedback-question">
        <legend>O que mais atrapalhou? <em>{needsIssues ? "obrigatório com nota até 3" : "opcional"}</em></legend>
        <div className="feedback-chips">{CASE_ISSUES.map((issue) => <button type="button" key={issue} aria-pressed={issues.includes(issue)}
          className={issues.includes(issue) ? "selected" : ""} onClick={() => toggleIssue(issue)}>{ISSUE_LABELS[issue]}</button>)}</div>
      </fieldset>

      <fieldset className="feedback-question">
        <legend>Tamanho da resposta <em>opcional</em></legend>
        <div className="segmented">{CASE_DEPTHS.map((item) => <button type="button" key={item} aria-pressed={depth === item}
          className={depth === item ? "selected" : ""} onClick={() => setDepth(depth === item ? undefined : item)}>{DEPTH_LABELS[item]}</button>)}</div>
      </fieldset>

      <label className="feedback-question">
        <span>O que um colega sênior faria diferente? <em>opcional</em></span>
        <span className="input-with-voice"><textarea rows={2} maxLength={CASE_NOTES_MAX} value={notes} onChange={(event) => setNotes(event.target.value)}
          placeholder="Ex.: faltou comparar com o Uruguai" /><DictationButton value={notes} onChange={setNotes} maxChars={CASE_NOTES_MAX} disabled={busy} label="Ditar o comentário" /></span>
        <small>{notes.length}/{CASE_NOTES_MAX} · identificadores pessoais são removidos antes de qualquer uso</small>
      </label>

      {confirming && <p className="feedback-conflict" role="alert"><CircleAlert size={14} />As respostas parecem se contradizer ({OUTCOME_LABELS[outcome!].toLocaleLowerCase("pt-BR")} com nota {rating}). Confirme para enviar assim — os pontos marcados continuam valendo.</p>}
      {skipStreak >= SKIP_STREAK_WARNING && <p className="feedback-streak"><CircleAlert size={14} />Você pulou as {skipStreak} últimas avaliações. Elas ajustam as próximas respostas da pesquisa.</p>}

      <button className="primary-button wide" disabled={!ready || busy}>{busy ? <RefreshCw className="spin" size={17} /> : confirming ? "Confirmar e concluir" : "Enviar e concluir caso"}</button>
      <button type="button" className="feedback-skip" disabled={busy} onClick={() => void run(onSkip)}>Pular a avaliação deste caso</button>
    </motion.form>
  </motion.div>;
}
